import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, GitCompare } from "lucide-react";
import { useNav, VIEWS } from "@/lib/nav";

const THRESHOLDS = [0.8, 0.85, 0.9, 0.95];
const STAT_KEYS = ["COST", "RNG", "AOE", "POW", "DUR", "OFF"];

function DiffText({ diff, side }) {
  return (
    <p className="text-sm leading-relaxed">
      {diff.map((seg, i) => {
        if (seg.op === "equal") return <span key={i}>{seg.text}</span>;
        if (side === "a" && seg.op === "removed")
          return <mark key={i} className="bg-rose-900/60 text-rose-100 rounded px-0.5">{seg.text}</mark>;
        if (side === "b" && seg.op === "added")
          return <mark key={i} className="bg-emerald-900/60 text-emerald-100 rounded px-0.5">{seg.text}</mark>;
        return null;
      })}
    </p>
  );
}

function SpellSide({ spell, diff, side, statDiffs, testId }) {
  const { navigate } = useNav();
  return (
    <div className="rounded border border-border bg-background/40 p-3 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <button className="font-semibold hover:text-primary hover:underline" onClick={() => navigate(VIEWS.rule(spell.id))} data-testid={`${testId}-link`}>
          {spell.name}
        </button>
        <span className="text-[11px] text-muted-foreground">{spell.card_count} card{spell.card_count === 1 ? "" : "s"}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {spell.factions.map((f) => (
          <Badge key={f} variant="outline" className="text-[10px] text-amber-300 border-amber-800/50">{f}</Badge>
        ))}
      </div>
      <div className="inline-flex gap-3 rounded border border-emerald-800/50 bg-emerald-950/20 px-2.5 py-1">
        {STAT_KEYS.map((k) => (
          <div key={k} className={`text-center leading-tight ${statDiffs.includes(k) ? "text-amber-300" : ""}`}>
            <div className="text-[9px] tracking-wide text-muted-foreground">{k}</div>
            <div className="text-sm font-semibold font-mono">{spell.stats[k] || "-"}</div>
          </div>
        ))}
      </div>
      <DiffText diff={diff} side={side} />
    </div>
  );
}

function OverlapPair({ pair, index }) {
  const testId = `overlap-pair-${index}`;
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Badge variant="outline" className={pair.kind === "same_name" ? "text-rose-300 border-rose-700/60" : "text-violet-300 border-violet-700/60"}>
            {pair.kind === "same_name" ? "Same name, different wording" : "Different names, near-identical wording"}
          </Badge>
          <span className="font-mono text-muted-foreground">{Math.round(pair.score * 100)}% text match</span>
          {pair.cross_faction && <Badge variant="outline" className="text-amber-300 border-amber-700/50">across factions</Badge>}
          {pair.stat_diffs.length > 0 && (
            <span className="text-amber-300">stats differ: {pair.stat_diffs.join(", ")}</span>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <SpellSide spell={pair.a} diff={pair.diff} side="a" statDiffs={pair.stat_diffs} testId={`${testId}-a`} />
          <SpellSide spell={pair.b} diff={pair.diff} side="b" statDiffs={pair.stat_diffs} testId={`${testId}-b`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function SpellOverlapPage() {
  const [minScore, setMinScore] = useState(0.85);
  const [crossOnly, setCrossOnly] = useState(false);
  const [kind, setKind] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    api.spellOverlap({ min_score: minScore, cross_faction_only: crossOnly, kind })
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [minScore, crossOnly, kind]);

  return (
    <div className="space-y-4" data-testid="spell-overlap-page">
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-violet-400" />
            <h2 className="text-xl font-semibold">Spell overlap finder</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Spells that share a name but are worded differently, or have different names but near-identical text — likely copy-paste drift.
            Removed words are red on the left, added words green on the right; differing stats are amber.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-1" data-testid="overlap-threshold">
              <span className="text-xs text-muted-foreground mr-1">Min. text match</span>
              {THRESHOLDS.map((t) => (
                <Button key={t} size="sm" variant={minScore === t ? "default" : "outline"} onClick={() => setMinScore(t)} data-testid={`overlap-threshold-${Math.round(t * 100)}`}>
                  {Math.round(t * 100)}%
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[["", "All"], ["same_name", "Same name"], ["similar_text", "Similar text"]].map(([v, l]) => (
                <Button key={v || "all"} size="sm" variant={kind === v ? "default" : "outline"} onClick={() => setKind(v)} data-testid={`overlap-kind-${v || "all"}`}>
                  {l}
                </Button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={crossOnly} onCheckedChange={setCrossOnly} data-testid="overlap-cross-faction-toggle" />
              Only across factions
            </label>
            <span className="text-xs text-muted-foreground ml-auto" data-testid="overlap-count">
              {data ? `${data.total} pair${data.total === 1 ? "" : "s"}` : ""}
            </span>
          </div>
        </CardContent>
      </Card>
      {error && <p className="text-sm text-rose-300" data-testid="overlap-error">{error}</p>}
      {!data && !error && (
        <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Comparing every spell…</p>
      )}
      {data?.items.length === 0 && <p className="text-sm text-muted-foreground italic">No overlapping spells at this threshold.</p>}
      {data?.items.map((p, i) => <OverlapPair key={`${p.a.id}-${p.b.id}`} pair={p} index={i} />)}
    </div>
  );
}
